const VERIFIED_AUTHORITY = 'Verified Authority';
const VERIFIED_GOVERNMENT = 'Verified Government Source';
const MATTER_FILE = 'Matter File';
const JOLLY_MEMORY = 'Jolly-Derived Matter Memory';
const EXTERNAL = 'External / Unverified';
const INFERENCE = 'Inference';

export const VERIFICATION_BANDS = {
  verified_primary: {
    label: 'Verified Primary',
    shortLabel: 'Primary',
    description: 'Official primary law or court text suitable for direct legal reliance.',
  },
  verified_secondary: {
    label: 'Verified Secondary',
    shortLabel: 'Secondary',
    description: 'Reporter-backed or verified secondary support that still needs forum-specific use.',
  },
  official_portal: {
    label: 'Official Portal / Partial',
    shortLabel: 'Portal',
    description: 'Official portal or metadata support that may need direct document confirmation.',
  },
  matter_file: {
    label: 'Matter File',
    shortLabel: 'Matter',
    description: 'Private matter record or uploaded file.',
  },
  reviewed_memory: {
    label: 'Reviewed Memory',
    shortLabel: 'Memory',
    description: 'Human-reviewed voice-derived matter memory.',
  },
  external: {
    label: 'External / Unverified',
    shortLabel: 'External',
    description: 'Unverified material that should not quietly drive conclusions.',
  },
  inferred: {
    label: 'Inference',
    shortLabel: 'Inference',
    description: 'Reasoned synthesis rather than direct source text.',
  },
};

export const TRUST_ORDER = [
  VERIFIED_AUTHORITY,
  VERIFIED_GOVERNMENT,
  MATTER_FILE,
  JOLLY_MEMORY,
  EXTERNAL,
  INFERENCE,
];

export const TRUST_META = {
  [VERIFIED_AUTHORITY]: {
    shortLabel: 'Authority',
    background: 'rgba(34, 197, 94, 0.12)',
    border: '1px solid rgba(34, 197, 94, 0.2)',
    color: '#15803d',
    description: 'Reporter-backed authority from Indian legal sources.',
  },
  [VERIFIED_GOVERNMENT]: {
    shortLabel: 'Gov',
    background: 'rgba(20, 184, 166, 0.12)',
    border: '1px solid rgba(20, 184, 166, 0.2)',
    color: '#0f766e',
    description: 'Official government or regulator material.',
  },
  [MATTER_FILE]: {
    shortLabel: 'Matter file',
    background: 'rgba(59, 130, 246, 0.12)',
    border: '1px solid rgba(59, 130, 246, 0.18)',
    color: '#1d4ed8',
    description: 'Uploaded matter material or file-backed context.',
  },
  [JOLLY_MEMORY]: {
    shortLabel: 'Jolly',
    background: 'rgba(245, 158, 11, 0.14)',
    border: '1px solid rgba(245, 158, 11, 0.2)',
    color: '#b45309',
    description: 'Reviewed voice-derived matter memory.',
  },
  [EXTERNAL]: {
    shortLabel: 'External',
    background: 'rgba(148, 163, 184, 0.12)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    color: 'var(--text-secondary)',
    description: 'Unverified material that should not silently drive legal conclusions.',
  },
  [INFERENCE]: {
    shortLabel: 'Inference',
    background: 'rgba(249, 115, 22, 0.12)',
    border: '1px solid rgba(249, 115, 22, 0.2)',
    color: '#c2410c',
    description: 'Reasoned synthesis rather than source-backed material.',
  },
};

function normalizeLabelValue(rawLabel) {
  const label = String(rawLabel || '').trim();
  if (!label) return '';
  const lowered = label.toLowerCase();

  if (lowered === VERIFIED_AUTHORITY.toLowerCase()) return VERIFIED_AUTHORITY;
  if (lowered === VERIFIED_GOVERNMENT.toLowerCase()) return VERIFIED_GOVERNMENT;
  if (lowered === MATTER_FILE.toLowerCase()) return MATTER_FILE;
  if (lowered === JOLLY_MEMORY.toLowerCase()) return JOLLY_MEMORY;
  if (lowered === EXTERNAL.toLowerCase()) return EXTERNAL;
  if (lowered === INFERENCE.toLowerCase()) return INFERENCE;
  return label;
}

function looksLikeGovernmentSource(source = {}) {
  const family = String(source.source_family || source.sourceFamily || '').toLowerCase();
  const trust = String(source.trust_label || source.trustLabel || '').toLowerCase();
  const url = String(source.url || '').toLowerCase();
  return (
    family.includes('government')
    || trust.includes('government')
    || url.includes('.gov.in')
    || url.includes('.nic.in')
    || url.includes('indiacode.nic.in')
  );
}

export function normalizeSourceTrustLabel(sourceOrLabel) {
  if (!sourceOrLabel) return EXTERNAL;
  if (typeof sourceOrLabel === 'string') return normalizeLabelValue(sourceOrLabel) || EXTERNAL;

  const explicit = normalizeLabelValue(sourceOrLabel.trust_label || sourceOrLabel.trustLabel);
  if (explicit) return explicit;

  const sourceType = String(sourceOrLabel.sourceType || sourceOrLabel.source_type || '').toLowerCase();
  if (sourceType === 'jolly') return JOLLY_MEMORY;
  if (sourceType === 'document') return MATTER_FILE;
  if (looksLikeGovernmentSource(sourceOrLabel)) return VERIFIED_GOVERNMENT;

  const url = String(sourceOrLabel.url || '').toLowerCase();
  if (url.includes('indiankanoon.org')) return VERIFIED_AUTHORITY;

  if (sourceOrLabel.pageNumber || sourceOrLabel.page_number || sourceOrLabel.fileName || sourceOrLabel.filename) {
    return MATTER_FILE;
  }

  return EXTERNAL;
}

export function getSourceVerificationBand(sourceOrLabel) {
  if (!sourceOrLabel || typeof sourceOrLabel === 'string') {
    const label = normalizeSourceTrustLabel(sourceOrLabel);
    if (label === VERIFIED_AUTHORITY) return { id: 'verified_secondary', ...VERIFICATION_BANDS.verified_secondary };
    if (label === VERIFIED_GOVERNMENT) return { id: 'verified_primary', ...VERIFICATION_BANDS.verified_primary };
    if (label === MATTER_FILE) return { id: 'matter_file', ...VERIFICATION_BANDS.matter_file };
    if (label === JOLLY_MEMORY) return { id: 'reviewed_memory', ...VERIFICATION_BANDS.reviewed_memory };
    if (label === INFERENCE) return { id: 'inferred', ...VERIFICATION_BANDS.inferred };
    return { id: 'external', ...VERIFICATION_BANDS.external };
  }

  const trustClass = String(sourceOrLabel.trust_class || sourceOrLabel.trustClass || '').toLowerCase();
  const sourceType = String(sourceOrLabel.sourceType || sourceOrLabel.source_type || '').toLowerCase();
  const reviewState = String(sourceOrLabel.reviewState || sourceOrLabel.review_state || '').toLowerCase();

  if (trustClass === 'official_primary') return { id: 'verified_primary', ...VERIFICATION_BANDS.verified_primary };
  if (trustClass === 'verified_reporter') return { id: 'verified_secondary', ...VERIFICATION_BANDS.verified_secondary };
  if (trustClass === 'official_portal') return { id: 'official_portal', ...VERIFICATION_BANDS.official_portal };
  if (trustClass === 'matter_record' || sourceType === 'document') return { id: 'matter_file', ...VERIFICATION_BANDS.matter_file };
  if (trustClass === 'reviewed_memory' || (sourceType === 'jolly' && ['reviewed', 'approved', 'accepted'].includes(reviewState))) {
    return { id: 'reviewed_memory', ...VERIFICATION_BANDS.reviewed_memory };
  }
  if (trustClass === 'inference') return { id: 'inferred', ...VERIFICATION_BANDS.inferred };

  const verificationStatus = String(sourceOrLabel.verification_status || sourceOrLabel.verificationStatus || '').toLowerCase();
  if (verificationStatus === 'partial') return { id: 'official_portal', ...VERIFICATION_BANDS.official_portal };

  const label = normalizeSourceTrustLabel(sourceOrLabel);
  if (label === VERIFIED_AUTHORITY) return { id: 'verified_secondary', ...VERIFICATION_BANDS.verified_secondary };
  if (label === VERIFIED_GOVERNMENT) return { id: 'verified_primary', ...VERIFICATION_BANDS.verified_primary };
  if (label === MATTER_FILE) return { id: 'matter_file', ...VERIFICATION_BANDS.matter_file };
  if (label === JOLLY_MEMORY) return { id: 'reviewed_memory', ...VERIFICATION_BANDS.reviewed_memory };
  if (label === INFERENCE) return { id: 'inferred', ...VERIFICATION_BANDS.inferred };
  return { id: 'external', ...VERIFICATION_BANDS.external };
}

export function getSourceTrustMeta(sourceOrLabel) {
  const label = normalizeSourceTrustLabel(sourceOrLabel);
  return {
    label,
    ...(TRUST_META[label] || TRUST_META[EXTERNAL]),
  };
}

export function dedupeSourceList(items = []) {
  const seen = new Set();
  return (items || []).filter((item) => {
    if (!item || typeof item !== 'object') return false;
    const key = [
      item.sourceId,
      item.artifactId,
      item.artifact_id,
      item.fileId,
      item.file_id,
      item.upload_id,
      item.id,
      item.url,
      item.previewUrl,
      item.preview_url,
      `${item.fileName || item.filename || item.title || 'source'}:${item.pageNumber || item.page_number || ''}`,
    ].find(Boolean);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function buildTrustSummary(items = []) {
  const counts = new Map();

  dedupeSourceList(items).forEach((item) => {
    const label = normalizeSourceTrustLabel(item);
    counts.set(label, (counts.get(label) || 0) + 1);
  });

  return TRUST_ORDER
    .map((label) => {
      const count = counts.get(label) || 0;
      if (!count) return null;
      const meta = getSourceTrustMeta(label);
      return {
        label,
        count,
        shortLabel: meta.shortLabel,
        description: meta.description,
        background: meta.background,
        border: meta.border,
        color: meta.color,
      };
    })
    .filter(Boolean);
}

export function getSourceDisplayTitle(source = {}) {
  return String(
    source.title
      || source.fileName
      || source.filename
      || source.originalName
      || source.docsource
      || 'Matter source',
  ).trim();
}

export function getSourcePreviewHref(source = {}, options = {}) {
  const uploadPreviewBuilder = options.uploadPreviewBuilder;
  if (source.preview_url) return source.preview_url;
  if (source.previewUrl) return source.previewUrl;
  if (source.url) return source.url;
  if (source.upload_id && typeof uploadPreviewBuilder === 'function') {
    return uploadPreviewBuilder(source.upload_id);
  }
  return '';
}
